import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  mainProfilePhotoImageInputContainer,
  mainProfilePhotoImageInputText,
  profilePhotoOrderLabel,
  profilePhotosImageInput,
  profilePhotosMainCard,
  profilePhotosSecondaryCard,
  profilePhotosSelectedImage,
  profilePhotosSelectImagePlus
} from '../../styles';

import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader';
import CircularProgressBar from '@/components/ProfileData/ProfilePhotos/components/CircularProgress';
import DeleteButton from '@/components/ProfileData/ProfilePhotos/components/DeleteButton';
import PhotoStateLabel from '@/components/ProfileData/ProfilePhotos/components/PhotoStateLabel';
import ProfilePhotosLoader from '@/components/ProfileData/ProfilePhotos/components/ProfilePhotosLoader';
import CameraShape from '@/components/common/svg/shapes/CameraShape';
import { FileData } from '@/shared/interface/userRegistrationData';
import { usePhotoDeleteConfirm } from '@/hooks/usePhotoDeleteConfirm.ts';
import { PhotoKind } from '@/shared/type/userDataFields.ts';

type PhotoSlotProps = {
  photo?: FileData;
  index: number;
  isMain?: boolean;
  isSaving?: boolean[];
  isEdit?: boolean;
  isOtherUploading?: boolean;
  progress: number;
  isDeleting: boolean;
  onFileChange: (event: ChangeEvent<HTMLInputElement>, index: number) => void;
  onDelete: (index: number) => void;
  canDelete: (photo: FileData) => boolean;
};

export const PhotoSlot = ({
  photo,
  index,
  isMain = false,
  isEdit = false,
  isSaving,
  isOtherUploading = false,
  progress,
  isDeleting,
  onFileChange,
  onDelete,
  canDelete
}: PhotoSlotProps) => {
  const { t } = useTranslation();
  const orderLabel = isMain ? t('main_photo') : (index + 1).toString();

  const confirmDelete = usePhotoDeleteConfirm(onDelete);

  const cardStyle = isMain
    ? profilePhotosMainCard(!!photo)
    : profilePhotosSecondaryCard(!!photo);

  return (
    <div style={cardStyle}>
      {isDeleting ? (
        <>
          <ProfilePhotosLoader />
          <span style={profilePhotoOrderLabel}>{orderLabel}</span>
        </>
      ) : photo ? (
        <>
          {progress > 0 && (
            <CircularProgressBar progress={progress} size={50} />
          )}
          <ImageWithLoader
            key={index}
            src={photo.url[PhotoKind.KIND_PRIMARY]}
            thumbhash={photo?.thumbhash}
            alt="Preview"
            style={profilePhotosSelectedImage}
            customLoader={<ProfilePhotosLoader />}
          />
          <span style={profilePhotoOrderLabel}>{orderLabel}</span>
          {canDelete(photo) ? (
            !(isSaving?.some((p) => p) || isOtherUploading) && (
              <DeleteButton
                handleDelete={() =>
                  isEdit ? confirmDelete(index) : onDelete(index)
                }
              />
            )
          ) : (
            <PhotoStateLabel />
          )}
        </>
      ) : (
        <>
          {isMain ? (
            <div style={mainProfilePhotoImageInputContainer}>
              <CameraShape />
              <span style={mainProfilePhotoImageInputText}>
                {t('discovery.card.placeholder.insufficient_data.btn_label')}
              </span>
            </div>
          ) : (
            <span style={profilePhotosSelectImagePlus}>+</span>
          )}
          <span style={profilePhotoOrderLabel}>{orderLabel}</span>
          <input
            type="file"
            disabled={isSaving?.some((p) => p) || isOtherUploading}
            onChange={(event) => onFileChange(event, index)}
            accept="image/*"
            style={profilePhotosImageInput}
          />
        </>
      )}
    </div>
  );
};
